import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import styles from './NotificationItem.module.scss';
import { NotificationItemProps } from './NotificationCenter.types';
import { NOTIFICATION_CATEGORY, NOTIFICATION_CATEGORY_LABEL, NOTIFICATION_VERB } from './NotificationCenter.constants';
import { getInitialsFromName } from '../../utils/utils';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import DoneAllIcon from '../../assets/Svg/DoneAllIcon';
import UnreadNotificationsIcon from '../../assets/Svg/UnreadNotificationsIcon';
import { useNotificationClick } from './useNotificationClick';

dayjs.extend(relativeTime);

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onItemAction, onMarkRead, onMarkUnread }) => {
    const { id, actorName, companyName, lenderName, comment, isRead, isMention, createdAt, category } = notification;

    const { route, handleNotificationClick } = useNotificationClick(notification);

    const verb = isMention ? NOTIFICATION_VERB.MENTIONED : NOTIFICATION_VERB.ADDED;
    const isDealLike = category === NOTIFICATION_CATEGORY.DEAL || category === NOTIFICATION_CATEGORY.INVESTOR_DEAL;
    const categoryLabel = isDealLike ? NOTIFICATION_CATEGORY_LABEL.DEAL : NOTIFICATION_CATEGORY_LABEL.LEAD;

    const handleRowClick = () => {
        handleNotificationClick();
        // Always close the panel, even when the notification lacks ids for routing
        // (old pre-Phase-2 audits). Read state is NOT touched here — only the explicit
        // Mark read / Mark unread buttons change it.
        onItemAction();
    };

    const handleViewThreadClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        handleRowClick();
    };

    const handleMarkRead = (e: React.MouseEvent) => {
        e.stopPropagation();
        onMarkRead(id);
    };

    const handleMarkUnread = (e: React.MouseEvent) => {
        e.stopPropagation();
        onMarkUnread(id);
    };

    // The row itself is a mouse-only convenience target. It deliberately has no
    // role/tabIndex: it contains real <button>s (View thread, Mark read/unread), and
    // nesting interactive controls inside role="button" is invalid ARIA. Keyboard
    // users reach the same actions through those buttons.
    return (
        <div className={`${styles.NotificationItem} ${!isRead ? styles.Unread : ''}`} onClick={handleRowClick}>
            <div className={styles.Avatar}>{getInitialsFromName(actorName)}</div>
            <div className={styles.Content}>
                <p className={styles.Header}>
                    <strong>{actorName}</strong>
                    {` ${verb} on ${categoryLabel}`}
                </p>
                {companyName && (
                    <div className={styles.CompanyLine}>
                        <span className={styles.CompanyLink}>{companyName}</span>
                        {isDealLike && lenderName && <span className={styles.LenderSuffix}>{` <> ${lenderName}`}</span>}
                    </div>
                )}
                {comment && <div className={styles.CommentBox} dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment) }} />}
                <div className={styles.Footer}>
                    <span className={styles.Timestamp}>{dayjs(createdAt).fromNow()}</span>
                    <span className={styles.FooterSep} aria-hidden="true">
                        •
                    </span>
                    <button type="button" className={styles.ViewThreadBtn} onClick={handleViewThreadClick} disabled={!route}>
                        View thread
                    </button>
                </div>
            </div>
            {!isRead ? (
                <button type="button" className={styles.MarkReadBtn} onClick={handleMarkRead}>
                    <DoneAllIcon color="currentColor" />
                    Mark read
                </button>
            ) : (
                <button type="button" className={styles.MarkUnreadBtn} onClick={handleMarkUnread}>
                    <UnreadNotificationsIcon color="currentColor" />
                    Mark unread
                </button>
            )}
        </div>
    );
};

export default NotificationItem;
